import React from 'react'

import { CustomSpineEvents } from '../../../types/shared'
import ChartModel from '../../models/chart'
import ChartLayout from '../../models/chart_layout'
import { AutoSaveState } from '../../models/constants'
import DrawingsInternalStore from '../../models/drawings-internal-store'
import Element from '../../models/element'
import { usePrevious } from '../../utils/use-prev-state'
import { DRAWINGS_UPDATE_INTERVAL_MS } from './constants'
import { useDrawingAutoSaveControls } from './use-drawing-auto-save-controls'
import { getInitialStoreDrawings } from './utils'

function useDrawingAutoSave(chartLayout: ChartLayout) {
  const {
    onElementChange,
    deleteAllAutoSavedElements,
    checkDrawingUpdate,
    syncChartLayoutDrawings,
    saveLatestChanges,
    isAutoSaveActive,
  } = useDrawingAutoSaveControls(chartLayout)
  const drawingsInternalStore = React.useMemo(() => chartLayout.drawingsInternalStore()!, [chartLayout])
  const prevIsAutoSaveActive = usePrevious(isAutoSaveActive)

  React.useEffect(() => {
    let previousElementsJson = JSON.stringify(drawingsInternalStore.elements)
    const handleDrawingsInternalStoreChange = (newDrawingInternalStore: DrawingsInternalStore) => {
      if (JSON.stringify(newDrawingInternalStore.elements) !== previousElementsJson) {
        previousElementsJson = JSON.stringify(drawingsInternalStore.elements)
        saveLatestChanges()
      }
    }

    drawingsInternalStore.bind('change', handleDrawingsInternalStoreChange)

    return () => {
      drawingsInternalStore.unbind('change', handleDrawingsInternalStoreChange)
    }
  }, [saveLatestChanges, drawingsInternalStore])

  React.useEffect(() => {
    // Restore initial drawings from server on page load
    drawingsInternalStore.updateAttributes({ elements: getInitialStoreDrawings() ?? [] })

    // We want to trigger the effect only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (!isAutoSaveActive) {
      chartLayout.drawingsInternalStore().updateAttributes({ autoSaveState: AutoSaveState.Off })
      return
    }
    chartLayout.drawingsInternalStore().updateAttributes({ autoSaveState: AutoSaveState.Saved })
    if (!prevIsAutoSaveActive && isAutoSaveActive) {
      checkDrawingUpdate(true)
    }

    let tickers: string[] = chartLayout.getAllCharts().map((chart) => chart.quote()?.getRawTicker() ?? '')

    const onChartChange = () => {
      const newTickers = chartLayout.getAllCharts().map((chart) => chart.quote()?.getRawTicker() ?? '')
      if (JSON.stringify(tickers) !== JSON.stringify(newTickers)) {
        tickers = newTickers
        checkDrawingUpdate(true)
      }
    }
    const onIndicatorChange = () => {
      checkDrawingUpdate(true)
    }

    ChartModel.bind('change', onChartChange)
    ChartModel.bind(CustomSpineEvents.IndicatorsChange, onIndicatorChange)
    Element.bind('change', onElementChange)

    chartLayout.getAllElements().forEach((element) => {
      // Adds drawings to internal store if isAutoSaveActive and they are not in the store yet
      if (!drawingsInternalStore.elements.find((drawing) => drawing.elementId === element.elementId)) {
        onElementChange(element)
      }
    })

    drawingsInternalStore.elements.forEach(syncChartLayoutDrawings)
    const checkDrawingUpdateInterval = setInterval(checkDrawingUpdate, DRAWINGS_UPDATE_INTERVAL_MS)

    return () => {
      clearInterval(checkDrawingUpdateInterval)
      ChartModel.unbind('change', onChartChange)
      ChartModel.unbind(CustomSpineEvents.IndicatorsChange, onIndicatorChange)
      Element.unbind('change', onElementChange)
    }
  }, [
    chartLayout,
    drawingsInternalStore,
    checkDrawingUpdate,
    onElementChange,
    syncChartLayoutDrawings,
    isAutoSaveActive,
    prevIsAutoSaveActive,
  ])

  return { deleteAllAutoSavedElements, isAutoSaveActive }
}

interface Props {
  chartLayoutModel: ChartLayout
}

export const withDrawingsAutoSave = (Component: any) => (props: Props) => {
  const { deleteAllAutoSavedElements, isAutoSaveActive } = useDrawingAutoSave(props.chartLayoutModel)

  return (
    <Component {...props} deleteAllAutoSavedElements={deleteAllAutoSavedElements} isAutoSaveActive={isAutoSaveActive} />
  )
}
